import request from '../../../util/SuperagentUtils';
import {
  adminInvoicesReportEndpoints,
  buyerInvoicesReportEndpoints,
  supplierInvoicesReportEndpoints,
} from '../../../requests/api-endpoints';
import type { AssistantAudience } from '../assistantCapabilities';
import type { AdminInvoicesSummary } from '../../../types/model/valueObject/invoice/AdminInvoicesSummary';
import type { BuyerInvoicesSummary } from '../../../types/model/valueObject/invoice/BuyerInvoicesSummary';

/**
 * Invoice searches for the invoice lookups.
 *
 * Each audience has its own invoice search, scoped by the backend to what the
 * caller may see: a buyer's invoices, a supplier's, or — for staff — the whole
 * platform's. All three accept `transactionNumber` and `bookingNumber`, and match
 * both **partially** (SQL `LIKE '%n%'`), so a search for invoice 1234567 can also
 * return 11234567. Callers must pick the exact match out of the page rather than
 * trust the first row; the page is sized so the exact one is in it.
 *
 * No date range is sent. The date filters are optional server-side, and a named
 * invoice or booking should be found however old it is.
 *
 * The status queues ("which payouts are on hold") filter the admin and buyer
 * searches on a status group, and send dates only when the question named a
 * period. The supplier search has no status filter.
 */

/** One invoice row. Loose and local — only the fields the skills read. */
export interface InvoiceLookupRow {
  transactionNumber?: number | string;
  bookingNumber?: string;
  transactionType?: string;
  status?: string;
  buyerTransactionStatus?: string;
  supplierTransactionStatus?: string;
  statusNotes?: string;
  paymentMessage?: string;
  buyerAmount?: number;
  supplierAmount?: number;
  securspaceFees?: number;
  paymentProcessorFees?: number;
  transactionTaxAmount?: number;
  serviceStartDate?: string;
  serviceEndDate?: string;
  startDate?: string;
  endDate?: string;
  createdOn?: string;
  paymentCreatedOn?: string;
  paymentCompletedOn?: string;
  payoutCreatedOn?: string;
  payoutCompletedOn?: string;
  manualPaymentCompletedOn?: string;
  supplierPayoutOnHold?: boolean;
  refundForTransactionNumber?: number | string;
  refundTransactionNumbers?: string;
  reasonForRefund?: string;
  paymentMethodDescription?: string;
  paymentType?: string;
  paymentProcessor?: string;
  buyerCompanyName?: string;
  supplierCompanyName?: string;
  locationName?: string;
  bookingStatus?: string;
}

interface InvoicePage {
  content?: InvoiceLookupRow[];
  count?: number;
}

const SEARCH_ENDPOINT: Record<AssistantAudience, string> = {
  admin: adminInvoicesReportEndpoints.search,
  supplier: supplierInvoicesReportEndpoints.report,
  buyer: buyerInvoicesReportEndpoints.search,
};

/** The summary each audience gets. Add `supplier` here and to {@link SUMMARY_ENDPOINT} once its endpoint exists. */
export interface InvoicesSummaryByAudience {
  admin: AdminInvoicesSummary;
  buyer: BuyerInvoicesSummary;
}

const SUMMARY_ENDPOINT: Record<keyof InvoicesSummaryByAudience, string> = {
  admin: adminInvoicesReportEndpoints.summary,
  buyer: buyerInvoicesReportEndpoints.summary,
};

/**
 * Rows fetched to find one invoice among partial matches. Invoice numbers are
 * sequential, so the numbers that merely *contain* a seven-digit one are few.
 */
export const INVOICE_MATCH_PAGE_SIZE = 25;

/** Most invoices listed for one booking — the server's page-size ceiling. */
export const BOOKING_INVOICES_PAGE_SIZE = 100;

/**
 * Most pages read for one booking. The search matches booking numbers partially, so
 * its count is not this booking's; pages are read until every match has been seen.
 */
export const MAX_BOOKING_INVOICE_PAGES = 5;

export const getInvoicesSummary = async <A extends keyof InvoicesSummaryByAudience>(
  audience: A,
  filter: Record<string, string>,
): Promise<InvoicesSummaryByAudience[A]> => {
  const response = await request.get(SUMMARY_ENDPOINT[audience]).query(filter);
  return response.body as InvoicesSummaryByAudience[A];
};

const searchInvoices = async (
  audience: AssistantAudience,
  filter: Record<string, string>,
  size: number,
  sortDir: 'asc' | 'desc' = 'desc',
  page = 0,
): Promise<InvoicePage> => {
  // Newest first by default on every audience's search, so a page cut short by
  // `size` holds the most recent invoices rather than whatever the default sort is.
  const response = await request
    .get(SEARCH_ENDPOINT[audience])
    .query({ ...filter, sortBy: 'TRANSACTION_NUMBER', sortDir, page, size });
  return (response?.body as InvoicePage | undefined) ?? {};
};

/**
 * The invoice numbered exactly `invoiceNumber`, or null when there is none.
 *
 * The search matches invoice numbers partially, and every other number containing
 * these digits has more of them, so is larger. Sorted smallest first, the exact
 * invoice is the first row however many partial matches there are.
 */
export const findInvoiceByNumber = async (
  audience: AssistantAudience,
  invoiceNumber: string,
): Promise<InvoiceLookupRow | null> => {
  const { content = [] } = await searchInvoices(
    audience,
    { transactionNumber: invoiceNumber },
    INVOICE_MATCH_PAGE_SIZE,
    'asc',
  );
  return content.find((row) => String(row.transactionNumber ?? '') === invoiceNumber) ?? null;
};

export interface BookingInvoices {
  rows: InvoiceLookupRow[];
  /** True when the booking has more invoices than one page holds. */
  truncated: boolean;
  /** When the booking's first invoice was created, looked up only when `rows` stop short of it. */
  firstCreatedOn?: string;
}

/**
 * The invoices on the booking numbered exactly `bookingNumber`, newest first,
 * with whether some matches were left unread.
 *
 * The server's count covers every booking whose number merely contains this one,
 * so comparing it with this booking's rows would call a complete list truncated.
 * Instead pages are read until every match has been seen, up to
 * {@link MAX_BOOKING_INVOICE_PAGES}; only matches left unread make it truncated.
 */
export const findInvoicesForBooking = async (
  audience: AssistantAudience,
  bookingNumber: string,
): Promise<BookingInvoices> => {
  const onBooking = (row: InvoiceLookupRow) => (row.bookingNumber ?? '').trim() === bookingNumber;
  const read: InvoiceLookupRow[] = [];
  let complete = false;
  for (let page = 0; page < MAX_BOOKING_INVOICE_PAGES && !complete; page++) {
    const { content = [], count } = await searchInvoices(
      audience,
      { bookingNumber },
      BOOKING_INVOICES_PAGE_SIZE,
      'desc',
      page,
    );
    read.push(...content);
    complete = content.length < BOOKING_INVOICES_PAGE_SIZE || read.length >= (count ?? read.length);
  }
  const rows = read.filter(onBooking);
  if (complete) return { rows, truncated: false };

  // A link to all of them has to open the invoice page far enough back to
  // include the first, which the newest-first pages did not reach.
  const { content: oldestFirst = [] } = await searchInvoices(
    audience,
    { bookingNumber },
    BOOKING_INVOICES_PAGE_SIZE,
    'asc',
  );
  return { rows, truncated: true, firstCreatedOn: oldestFirst.find(onBooking)?.createdOn };
};

/** The audiences whose invoice search can filter on a status group. The supplier's cannot. */
export type StatusQueueAudience = 'admin' | 'buyer';

/**
 * What puts an invoice in a queue: a status group, or — admin only — the flag an
 * admin sets with "Put payout on hold", which leaves the status untouched.
 */
export type QueueSelector = { status: string } | { payoutOnHold: true };

/**
 * Narrows a status queue. Dates are `MM/DD/YYYY` and bound the day an invoice was
 * created; the account ids are admin-only filters, and the buyer search also takes
 * one `transactionType`.
 */
export interface StatusQueueFilter {
  startDate?: string;
  endDate?: string;
  buyerAccountId?: string;
  supplierAccountId?: string;
  transactionType?: string;
}

export interface StatusQueue {
  /** The newest invoices in the status, at most one page of them. */
  rows: InvoiceLookupRow[];
  /** Every invoice in the status, which may be more than `rows` holds. */
  count: number;
  /** When the oldest was created, looked up only when `rows` stop short of it. */
  firstCreatedOn?: string;
}

/** Most invoices read to list and total one status — the server's page-size ceiling. */
export const STATUS_QUEUE_PAGE_SIZE = 100;

/**
 * The status each audience's search filters on. The admin search takes the
 * supplier-facing groups, which is what the admin Invoices page's Transaction
 * Status filter sends; the buyer search takes the buyer-facing ones.
 */
const STATUS_PARAM: Record<StatusQueueAudience, string> = {
  admin: 'supplierTransactionStatus',
  buyer: 'buyerTransactionStatus',
};

const queueParams = (
  audience: StatusQueueAudience,
  selector: QueueSelector,
  filter: StatusQueueFilter,
): Record<string, string> => {
  const params: Record<string, string> =
    'status' in selector ? { [STATUS_PARAM[audience]]: selector.status } : { supplierPayoutOnHold: 'true' };
  for (const [key, value] of Object.entries(filter)) {
    if (value) params[key] = value;
  }
  return params;
};

/**
 * The invoices in one queue, newest first, with how many there are in all. A
 * failed payment leaves its status once it is paid, so a queue is what is still
 * outstanding, however old.
 */
export const findInvoiceQueue = async (
  audience: StatusQueueAudience,
  selector: QueueSelector,
  filter: StatusQueueFilter = {},
): Promise<StatusQueue> => {
  const params = queueParams(audience, selector, filter);
  const { content = [], count = content.length } = await searchInvoices(audience, params, STATUS_QUEUE_PAGE_SIZE);
  if (count <= content.length) return { rows: content, count };

  // Like a booking's invoices, a link to all of them has to open the page far
  // enough back to include the oldest, which the newest-first page stops short of.
  const { content: oldestFirst = [] } = await searchInvoices(audience, params, 1, 'asc');
  return { rows: content, count, firstCreatedOn: oldestFirst[0]?.createdOn };
};

/** How many invoices are in one queue, without reading them. */
export const countInvoiceQueue = async (
  audience: StatusQueueAudience,
  selector: QueueSelector,
  filter: StatusQueueFilter = {},
): Promise<number> => {
  const { content = [], count = content.length } = await searchInvoices(
    audience,
    queueParams(audience, selector, filter),
    1,
  );
  return count;
};
